import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { ThemeProvider } from "./context/ThemeContext";
import Login from "./pages/Login";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import Dashboard from "./pages/Dashboard";
import TeamPage from "./pages/TeamPage";
import ClientView from "./pages/ClientView";
import ClientPortal from "./pages/ClientPortal";
import RequestsInbox from "./pages/RequestsInbox";
import InvoicesPage from "./pages/InvoicesPage";
import ProjectsPage from "./pages/ProjectsPage";
import ReportsPage from "./pages/ReportsPage";
import AttendancePage from "./pages/AttendancePage";
import ChatPage from "./pages/ChatPage";
import GlancePage from "./pages/GlancePage";
import SiteVisitsPage from "./pages/SiteVisitsPage";
import ProfileCompletionGate from "./pages/ProfileCompletionGate";
import AttendancePopup from "./components/AttendancePopup";
import BroadcastPopup from "./components/BroadcastPopup";
import SessionGuard from "./components/SessionGuard";
import MarketingAdminPage from "./pages/MarketingAdminPage";
// ---- Public marketing pages (premium editorial) ----
import MarketingHome from "./marketing/premium/PremiumHome";
import MarketingPortfolio from "./marketing/premium/PremiumPortfolio";
import MarketingProjectDetail from "./marketing/premium/PremiumProjectDetail";
import MarketingProperties from "./marketing/premium/PremiumProperties";
import MarketingPropertyDetail from "./marketing/premium/PremiumPropertyDetail";
import MarketingBuilders from "./marketing/premium/PremiumBuilders";
import MarketingExpertise from "./marketing/premium/PremiumExpertise";
import MarketingAbout from "./marketing/premium/PremiumAbout";
import MarketingContact from "./marketing/premium/PremiumContact";
import MarketingBlogList from "./marketing/premium/PremiumBlogList";
import MarketingBlogDetail from "./marketing/premium/PremiumBlogDetail";
import { Toaster } from "sonner";
import { useTheme } from "./context/ThemeContext";
import "./App.css";

const STAFF_HOME = "/board";

function ProtectedRoute({ children, adminOnly = false, managerOrAdmin = false, clientOnly = false }) {
  const { user } = useAuth();
  if (user === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-zinc-50">
        <div className="label-overline text-zinc-500 px-6 py-4">Loading…</div>
      </div>
    );
  }
  if (user === false) return <Navigate to="/login" replace />;
  if (user.role === "client" && !clientOnly) return <Navigate to="/portal" replace />;
  if (clientOnly && user.role !== "client") return <Navigate to={STAFF_HOME} replace />;
  if (adminOnly && user.role !== "admin") return <Navigate to={STAFF_HOME} replace />;
  if (managerOrAdmin && user.role !== "admin" && user.role !== "manager") return <Navigate to={STAFF_HOME} replace />;

  if (user.role === "member" && user.profile_complete === false) {
    return <ProfileCompletionGate />;
  }
  return (
    <>
      {children}
      {["admin", "manager", "member"].includes(user.role) && <AttendancePopup />}
      {["admin", "manager", "member"].includes(user.role) && <BroadcastPopup />}
    </>
  );
}

function RedirectIfAuthed({ children }) {
  const { user } = useAuth();
  if (user && user !== false) {
    if (user.role === "client") return <Navigate to="/portal" replace />;
    return <Navigate to={STAFF_HOME} replace />;
  }
  return children;
}

export default function App() {
  return (
    <div className="App">
      <ThemeProvider>
        <AuthProvider>
        <BrowserRouter>
          <SessionGuard />
          <Routes>
            {/* Public marketing site */}
            <Route path="/" element={<MarketingHome />} />
            <Route path="/projects" element={<MarketingPortfolio />} />
            <Route path="/projects/:id" element={<MarketingProjectDetail />} />
            <Route path="/properties" element={<MarketingProperties />} />
            <Route path="/properties/:id" element={<MarketingPropertyDetail />} />
            <Route path="/builders" element={<MarketingBuilders />} />
            <Route path="/expertise" element={<MarketingExpertise />} />
            <Route path="/about" element={<MarketingAbout />} />
            <Route path="/contact" element={<MarketingContact />} />
            <Route path="/blog" element={<MarketingBlogList />} />
            <Route path="/blog/:slug" element={<MarketingBlogDetail />} />
            <Route path="/studio" element={<Navigate to="/login" replace />} />

            {/* Client-facing */}
            <Route path="/c/:token" element={<ClientView />} />
            <Route path="/login" element={<RedirectIfAuthed><Login /></RedirectIfAuthed>} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/portal" element={<ProtectedRoute clientOnly><ClientPortal /></ProtectedRoute>} />

            {/* Internal Studio app (staff) */}
            <Route path="/board" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
            <Route path="/board/projects" element={<ProtectedRoute managerOrAdmin><ProjectsPage /></ProtectedRoute>} />
            <Route path="/reports" element={<ProtectedRoute managerOrAdmin><ReportsPage /></ProtectedRoute>} />
            <Route path="/team" element={<ProtectedRoute managerOrAdmin><TeamPage /></ProtectedRoute>} />
            <Route path="/requests" element={<ProtectedRoute><RequestsInbox /></ProtectedRoute>} />
            <Route path="/invoices" element={<ProtectedRoute managerOrAdmin><InvoicesPage /></ProtectedRoute>} />
            <Route path="/attendance" element={<ProtectedRoute><AttendancePage /></ProtectedRoute>} />
            <Route path="/chat" element={<ProtectedRoute><ChatPage /></ProtectedRoute>} />
            <Route path="/glance" element={<ProtectedRoute managerOrAdmin><GlancePage /></ProtectedRoute>} />
            <Route path="/site-visits" element={<ProtectedRoute><SiteVisitsPage /></ProtectedRoute>} />
            <Route path="/marketing-admin" element={<ProtectedRoute adminOnly><MarketingAdminPage /></ProtectedRoute>} />

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
        <ThemedToaster />
      </AuthProvider>
      </ThemeProvider>
    </div>
  );
}

/** Toaster whose theme follows the app-wide light/dark preference. */
function ThemedToaster() {
  const { theme } = useTheme();
  return <Toaster position="top-right" richColors closeButton theme={theme} />;
}
